import * as THREE from "three";
import { asteroidPlants, asteroidPulls, isGround, treeTrunk, type AsteroidBody, type Plant } from "@dyson/shared";
import { TREE_PX, type TreeGround } from "./treeArt";
import type { TreeDone, TreeJob } from "./treeWorker";

/** Behind the back tiles (so ground in front hides the roots), in front of nothing else on the rock. */
const TREE_Z = -0.4;
/** How far into the ground the root is set (blocks), so the trunk grows out of it rather than standing on it. */
const SINK = 0.9;
/** The furthest a root reaches down to ground that falls away under a tree (blocks). */
const ROOT_REACH = 6;

/** Draws the trees (see treeWorker), started the first time one's wanted. */
let worker: Worker | undefined;
/** What to do with each tree the worker hands back, by job id. */
const waiting = new Map<number, (done: TreeDone) => void>();
let nextJob = 1;

/** Have a tree drawn off the main thread; `then` gets it (unless it's been forgotten by then, see forget). */
function drawLater(job: Omit<TreeJob, "id">, then: (done: TreeDone) => void): number {
  if (!worker) {
    worker = new Worker(new URL("./treeWorker.ts", import.meta.url), { type: "module" });
    worker.onmessage = (e: MessageEvent<TreeDone>) => {
      const then = waiting.get(e.data.id);
      waiting.delete(e.data.id);
      then?.(e.data);
    };
  }
  const id = nextJob++;
  waiting.set(id, then);
  worker.postMessage({ ...job, id } satisfies TreeJob);
  return id;
}

interface TreeMesh {
  plant: Plant;
  mesh?: THREE.Mesh;
  /** How lit it is by the suns usually (see setSun). */
  lit?: number;
  /** Its drawing, while the worker has it. */
  job?: number;
}

/**
 * An asteroid's trees (see Plant.tree): each one sprite of pixel art (see
 * treeArt), stood on the ground at its base, heading out from it, lit as
 * the ground there is. Shown while the tree stands (its trunk tiles are
 * there), gone once it's felled or burned, back when it grows again. Each
 * appears once the worker has drawn it, a moment after the rock does.
 */
export class TreeViews {
  readonly group = new THREE.Group();
  private readonly trees: TreeMesh[] = [];
  /** How bright the suns are now, against usual (see setSun). */
  private sun = 1;
  /** Which tree each trunk tile is part of. */
  private readonly byTile = new Map<number, TreeMesh>();

  constructor(
    private readonly a: AsteroidBody,
    private readonly light: (x: number, y: number) => number,
  ) {
    for (const plant of asteroidPlants(a.def)) {
      if (!plant.tree) continue;
      const t: TreeMesh = { plant };
      this.trees.push(t);
      for (const i of plant.tiles) this.byTile.set(i, t);
      const { style, seed, blocks } = plant.tree;
      const ground = this.groundUnder(plant.tree, treeTrunk(style, seed, blocks).half);
      t.job = drawLater({ style, seed, blocks, ground }, (done) => {
        t.job = undefined;
        this.place(t, done);
      });
    }
  }

  /** Tiles changed: trees felled or grown back show or hide. */
  update(indices: number | readonly number[]): void {
    for (const i of typeof indices === "number" ? [indices] : indices) {
      const t = this.byTile.get(i);
      if (t?.mesh) t.mesh.visible = this.standing(t.plant);
    }
  }

  private standing(p: Plant): boolean {
    return p.tiles.some((i, k) => this.a.grid.tiles[i] === p.grown[k]);
  }

  private place(t: TreeMesh, art: TreeDone): void {
    const tree = t.plant.tree!;
    const tex = new THREE.DataTexture(art.data, art.width, art.height, THREE.RGBAFormat);
    // Sharp close up; blended from mip levels further out, so it doesn't shimmer (as the tiles are, see atlasTexture).
    tex.magFilter = THREE.NearestFilter;
    tex.minFilter = THREE.LinearMipmapLinearFilter;
    tex.generateMipmaps = true;
    tex.colorSpace = THREE.SRGBColorSpace;
    tex.needsUpdate = true;
    const w = art.width / TREE_PX;
    const h = art.height / TREE_PX;
    // The root at the origin, the tree up +y.
    const geometry = new THREE.PlaneGeometry(w, h).translate(w / 2 - art.rootX / TREE_PX, h / 2 - (art.height - art.rootY) / TREE_PX, 0);
    const material = new THREE.MeshBasicMaterial({ map: tex, alphaTest: 0.5 });
    // Lit as the ground it stands on (light is baked into the tiles the same way).
    t.lit = Math.min(1, Math.max(0.2, this.light(tree.x, tree.y)));
    material.color.setScalar(t.lit * this.sun);
    const mesh = new THREE.Mesh(geometry, material);
    const out = this.heading(tree);
    mesh.position.set(tree.x - Math.cos(out) * SINK, tree.y - Math.sin(out) * SINK, TREE_Z);
    mesh.rotation.z = out - Math.PI / 2;
    mesh.visible = this.standing(t.plant);
    t.mesh = mesh;
    this.group.add(mesh);
  }

  /** Which way a tree stands: on a rock that pulls, square to the grid, as its up is (see GRAVITY); generation headed them that way too. */
  private heading(tree: NonNullable<Plant["tree"]>): number {
    return asteroidPulls(this.a.def) ? Math.round(tree.out / (Math.PI / 2)) * (Math.PI / 2) : tree.out;
  }

  /**
   * How far the rock falls away under a tree, across it (see TreeGround):
   * from its root, down through the tiles to the first ground, out past where
   * its roots can reach (eleven times `half`, its trunk's half-width at the root).
   */
  private groundUnder(tree: NonNullable<Plant["tree"]>, half: number): TreeGround {
    const g = this.a.grid;
    const out = this.heading(tree);
    const [ux, uy] = [Math.cos(out), Math.sin(out)];
    const [ax, ay] = [Math.sin(out), -Math.cos(out)];
    const [rx, ry] = [tree.x - ux * SINK, tree.y - uy * SINK];
    const solid = (x: number, y: number) => {
      const [tx, ty] = [Math.floor(x), Math.floor(y)];
      return tx >= 0 && ty >= 0 && tx < g.w && ty < g.h && isGround(g.tiles[ty * g.w + tx]!);
    };
    const step = 0.5;
    const n = Math.ceil((half * 11 + 1) / step);
    const depth: number[] = [];
    let last = 0;
    for (let i = -n; i <= n; i++) {
      const [x, y] = [rx + ax * i * step, ry + ay * i * step];
      let d = 0;
      while (d <= ROOT_REACH && !solid(x - ux * d, y - uy * d)) d += 0.25;
      // No ground in reach (over the edge of the rock): as the last.
      last = d > ROOT_REACH ? last : d;
      depth.push(last);
    }
    return { step, depth };
  }

  /** How bright the suns are now, against usual (1): an outage dims the trees with the ground (see NIGHT). */
  setSun(k: number): void {
    if (Math.abs(k - this.sun) < 0.004) return;
    this.sun = k;
    for (const t of this.trees) if (t.mesh && t.lit !== undefined) (t.mesh.material as THREE.MeshBasicMaterial).color.setScalar(t.lit * k);
  }

  dispose(): void {
    for (const t of this.trees) {
      // Still being drawn: never mind it.
      if (t.job !== undefined) waiting.delete(t.job);
      if (!t.mesh) continue;
      t.mesh.geometry.dispose();
      const m = t.mesh.material as THREE.MeshBasicMaterial;
      m.map?.dispose();
      m.dispose();
    }
  }
}
